/**
 * 广告实体指标表（campaign / adset / ad）：真实花费 + 两列 ROAS + 行内快捷操作。
 *
 * 数据来源：`GET /v1/metrics/entities`（花费降序、游标分页）。指标口径**分列**：
 *   - `metaROAS`（口径 1）= 平台自报转化价值 ÷ 花费；
 *   - `UTM ROAS`（口径 3，参考）= 我们按 UTM 匹到的订单额 ÷ 花费；它只覆盖约 15% 订单，
 *     因此 UTM 列头同时标注整页覆盖率（来自响应的 `data.coverage`）。
 * 页面不做任何指标换算：所有数字（含 null）都来自后端契约。
 *
 * 写操作流程（与后端四道闸门对应，客户端不绕过任何一层）：
 *   行内/批量按钮 → 本地预算预检（仅预算）→ `POST /v1/actions { dryRun:true }`
 *   → 确认对话框展示逐条 planned/rejected + 将发送的请求 → 用户确认
 *   → `POST /v1/actions { dryRun:false, confirm:true }` → 逐条 succeeded/failed/rejected。
 *
 * 后端 403 `WRITES_DISABLED` 会被显示成「服务端未开启写操作」，而不是普通报错。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ActionPlanDialog, ACTION_LABEL } from "../components/ActionPlanDialog.js";
import { BudgetDialog } from "../components/BudgetDialog.js";
import { BulkActionBar } from "../components/BulkActionBar.js";
import { Chip } from "../components/Chip.js";
import { DataReadinessNote, SettlementWarning } from "../components/DataReadinessNote.js";
import { EntityTable } from "../components/EntityTable.js";
import { StateView, TableSkeleton } from "../components/StateView.js";
import { ENTITY_GRID_TEMPLATE, ENTITY_TABLE_WIDTH, supportsBudget } from "../data/entityColumns.js";
import {
  DEFAULT_ENTITY_FILTER,
  entityAccountOptions,
  entityLevelHint,
  entityStatusOptions,
  filterEntities,
  sortEntities,
  type EntityFilter,
  type EntitySortDir,
} from "../data/entitySelectors.js";
import { useDebouncedValue } from "../hooks/useDebouncedValue.js";
import { useDataReadiness } from "../hooks/useDataReadiness.js";
import { useEntityMetrics, type EntityMetricsRowPatch } from "../hooks/useEntityMetrics.js";
import { describeError } from "../hooks/useApiResource.js";
import { DiagnosticWarnings } from "../lib/diagnostics.js";
import { useRowHeight } from "../hooks/useRowHeight.js";
import { ApiClientError, apiPostActions } from "../lib/apiClient.js";
import type {
  AdEntityLevel,
  AdEntityRow,
  ActionPlanResult,
  ActionTargetInput,
  MetaActionName,
} from "../lib/apiTypes.js";
import {
  getLatestBenchResult,
  registerScrollBenchRunner,
  runScrollBench,
  setLatestBenchResult,
} from "../perf/scrollBench.js";
import { rum } from "../perf/rum.js";
import { formatFixed } from "../lib/format.js";
import { resolveDateRange, todayIso } from "../lib/dateRange.js";
import { useUiStore } from "../store/uiStore.js";
import { useContextStore } from "../store/contextStore.js";
import { shopsForBrand } from "../lib/globalContext.js";
import { apiFetchShops } from "../lib/apiClient.js";
import { useApiResource } from "../hooks/useApiResource.js";
import {
  resolveShopRequirement,
  shopRequiredBlocksContent,
  shopRequiredNotice,
} from "../lib/shopRequired.js";

const OVERSCAN = 8;
const PAGE_STEP = 20;
const ENTITY_PAGE_LIMIT = 200;

/** 指标窗口预设（天）；默认近 7 天（**不含今天**，见 `metricsRange`）。 */
const WINDOW_PRESETS: readonly { readonly value: number; readonly label: string }[] = [
  { value: 7, label: "近 7 天" },
  { value: 14, label: "近 14 天" },
  { value: 30, label: "近 30 天" },
];

/**
 * 客户端计算的业务日窗口。
 *
 * ⚠️ 2026-10-05 第十五批：**统一走 `dateRange.ts` 的 `resolveDateRange`**
 * （`[今天-N, 今天-1]`，**不含今天**，与广告管理 / 经营概览同一份口径）。
 * 此前这里自己用 UTC 日期减 `N-1` 天：既含今天，又是**第二套**日历算法
 * （UTC 切日 vs 本地日历日——东八区凌晨会差一天，是同一屏两个区间的高发区）。
 */
function metricsRange(days: number, now: Date): { readonly from: string; readonly to: string } {
  const resolved = resolveDateRange({ kind: "last_days", days }, now);
  // `last_days` 恒有解；兜底只为类型收窄（绝不用别的区间冒充）。
  return resolved === null ? { from: todayIso(now), to: todayIso(now) } : { from: resolved.from, to: resolved.to };
}

/** 列头 badge 要短（列宽只有 104px），完整口径说明放信息条。 */
const LEVELS: readonly { readonly value: AdEntityLevel; readonly label: string }[] = [
  { value: "campaign", label: "广告系列" },
  { value: "adset", label: "广告组" },
  { value: "ad", label: "广告" },
];

/**
 * 语言筛选（R24）：挂在广告系列上，生产已播种 en / es / de（22,940 系列）。
 * 筛选位置 = 页面内筛选条，且**写进 URL**（可分享、可刷新还原）。
 */
const LANGUAGES: readonly { readonly value: string; readonly label: string }[] = [
  { value: "all", label: "全部语言" },
  { value: "en", label: "en · 英语" },
  { value: "de", label: "de · 德语" },
  { value: "es", label: "es · 西语" },
];

interface ActionRequestInput {
  readonly action: MetaActionName;
  readonly targets: readonly ActionTargetInput[];
  readonly budgetMinorUnits: number | null;
  readonly label: string;
}

type Workflow =
  | { readonly phase: "idle" }
  | { readonly phase: "dry-run"; readonly request: ActionRequestInput }
  | { readonly phase: "preview"; readonly request: ActionRequestInput; readonly plan: ActionPlanResult }
  | { readonly phase: "executing"; readonly request: ActionRequestInput; readonly plan: ActionPlanResult }
  | {
      readonly phase: "results";
      readonly request: ActionRequestInput;
      readonly result: ActionPlanResult;
    }
  | {
      readonly phase: "error";
      readonly request: ActionRequestInput;
      readonly message: string;
      readonly writesDisabled: boolean;
    };

function toTarget(row: AdEntityRow): ActionTargetInput {
  return {
    accountId: row.accountId,
    level: row.level,
    entityId: row.id,
    currentBudgetMinorUnits: row.dailyBudgetCents,
  };
}

function budgetInputValue(row: AdEntityRow | undefined): string {
  const cents = row?.dailyBudgetCents ?? null;
  return cents === null ? "" : (cents / 100).toFixed(2);
}

export function AdsPage() {
  const rowHeight = useRowHeight();
  const pushToast = useUiStore((store) => store.pushToast);
  // 语言筛选来自 URL（R24：筛选位置 = 页面内筛选条；URL 是唯一真相，可分享/刷新还原）。
  const search = useSearch({ from: "/" });
  const navigate = useNavigate();
  const language = search.language === "all" ? undefined : search.language;

  const [level, setLevel] = useState<AdEntityLevel>("campaign");
  const [windowDays, setWindowDays] = useState(7);
  // 每次渲染重建 range 无妨：hook 的依赖是 from/to 字符串，不是对象身份。
  const range = useMemo(() => metricsRange(windowDays, new Date()), [windowDays]);
  /**
   * **数据就绪度（2026-10-05 第十七批）**：本页指标全部来自 Meta 实体事实
   * （`/v1/metrics/entities`），是**纯 Meta 视图**。窗口由页面内的下拉决定
   * （不是 URL 预设档），因此 `rangeKey="custom"`：就绪度只按 from/to 现算，
   * **不改写**用户选的窗口，只在顶部标注"仍在结算"。
   */
  const readiness = useDataReadiness({
    viewKind: "meta_only",
    rangeKey: "custom",
    from: range.from,
    to: range.to,
  });
  /**
   * ⭐ 2026-10-07「站点必选」（用户点名含本页；Lead 确认语义 = **该页数据按站点过滤**）。
   *
   * 本页此前**没有站点维度**（`/v1/metrics/entities` 支持 `shopId`，但前端从未带上）
   * ⇒ 这里从无到有加一维：判定 + 自动选中 + 阻断文案 + **把 `shopId` 真正带进请求**
   * （不是"加了门、数据仍跨店"）。
   */
  const globalShopId = useContextStore((state) => state.shopId);
  const setGlobalShopId = useContextStore((state) => state.setShopId);
  const brandId = useContextStore((state) => state.brandId);
  const shopsResource = useApiResource((signal) => apiFetchShops(signal), []);
  const visibleShopIds = useMemo(
    () =>
      (shopsResource.data === null ? [] : shopsForBrand(shopsResource.data.items, brandId)).map(
        (shop) => shop.shopId,
      ),
    [shopsResource.data, brandId],
  );
  const requirement = useMemo(
    () => resolveShopRequirement({ visibleShopIds, shopId: globalShopId }),
    [visibleShopIds, globalShopId],
  );
  /** 实际用于取数的站点：只有 `ready` 才取数（其余 ⇒ 阻断，不取数）。 */
  const effectiveShopId = requirement.kind === "ready" ? requirement.shopId : "";
  // 只可见一个店铺 ⇒ 自动选中一次（单向写 URL；不做双向 effect 互写）。
  useEffect(() => {
    if (requirement.kind === "auto_select") setGlobalShopId(requirement.shopId);
  }, [requirement, setGlobalShopId]);

  const metrics = useEntityMetrics(level, range, ENTITY_PAGE_LIMIT, language, effectiveShopId);

  const [filter, setFilter] = useState<EntityFilter>(DEFAULT_ENTITY_FILTER);
  const [queryInput, setQueryInput] = useState("");
  const debouncedQuery = useDebouncedValue(queryInput, 220);

  const [sortId, setSortId] = useState("name");
  const [sortDir, setSortDir] = useState<EntitySortDir>("asc");
  const [selection, setSelection] = useState<ReadonlySet<string>>(() => new Set<string>());
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [anchorIndex, setAnchorIndex] = useState(0);

  const [workflow, setWorkflow] = useState<Workflow>({ phase: "idle" });
  const [budgetTargets, setBudgetTargets] = useState<readonly AdEntityRow[] | null>(null);
  const [budgetInitialValue, setBudgetInitialValue] = useState("");

  const scrollElementRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setFilter((previous) => ({ ...previous, q: debouncedQuery }));
  }, [debouncedQuery]);

  const rows = metrics.rows;
  const filtered = useMemo(() => filterEntities(rows, filter), [rows, filter]);
  const sorted = useMemo(() => sortEntities(filtered, sortId, sortDir), [filtered, sortDir, sortId]);

  const accounts = useMemo(() => entityAccountOptions(rows), [rows]);
  const statuses = useMemo(() => entityStatusOptions(rows), [rows]);
  const rowById = useMemo(() => {
    const map = new Map<string, AdEntityRow>();
    for (const row of rows) map.set(row.id, row);
    return map;
  }, [rows]);

  const selectedRows = useMemo(
    () => [...selection].map((id) => rowById.get(id)).filter((row): row is AdEntityRow => row !== undefined),
    [rowById, selection],
  );
  const budgetEligible = selectedRows.length > 0 && selectedRows.every((row) => supportsBudget(row.level));
  const busy = workflow.phase === "dry-run" || workflow.phase === "executing";

  // URL 与后端游标都不支持服务端筛选/排序：这里的筛选只作用于「已加载的行」，UI 必须写清楚。
  const handleSort = useCallback(
    (columnId: string) => {
      setSortDir((previousDir) => (sortId === columnId && previousDir === "asc" ? "desc" : "asc"));
      setSortId(columnId);
    },
    [sortId],
  );

  const toggleSelect = useCallback(
    (index: number) => {
      const row = sorted[index];
      if (row === undefined) return;
      setSelection((previous) => {
        const next = new Set(previous);
        if (next.has(row.id)) next.delete(row.id);
        else next.add(row.id);
        return next;
      });
      setAnchorIndex(index);
    },
    [sorted],
  );

  const extendSelection = useCallback(
    (index: number) => {
      const ids = sorted.map((row) => row.id);
      const start = Math.min(anchorIndex, index);
      const end = Math.max(anchorIndex, index);
      setSelection((previous) => {
        const next = new Set(previous);
        for (let cursor = start; cursor <= end; cursor += 1) {
          const id = ids[cursor];
          if (id !== undefined) next.add(id);
        }
        return next;
      });
    },
    [anchorIndex, sorted],
  );

  const selectAll = useCallback(() => {
    setSelection(new Set(sorted.map((row) => row.id)));
  }, [sorted]);

  const clearSelection = useCallback(() => setSelection(new Set<string>()), []);

  // 重新拉取会重建行集合：顺手清掉选择，避免留下指向旧行集合的 id
  const reload = useCallback(() => {
    clearSelection();
    metrics.reload();
  }, [clearSelection, metrics]);

  const changeLevel = useCallback((next: AdEntityLevel) => {
    setLevel(next);
    setSelection(new Set<string>());
    setFilter(DEFAULT_ENTITY_FILTER);
    setQueryInput("");
    setFocusedIndex(0);
    setAnchorIndex(0);
  }, []);

  // 语言写进 URL（默认 "all" 不落进查询串）；换筛选清空选择，避免留下指向旧行集合的 id。
  const changeLanguage = useCallback(
    (value: string) => {
      setSelection(new Set<string>());
      void navigate({ to: "/", search: { ...search, language: value } });
    },
    [navigate, search],
  );

  // ---------------------------------------------------------- 写操作工作流

  const beginAction = useCallback(async (request: ActionRequestInput): Promise<void> => {
    setWorkflow({ phase: "dry-run", request });
    try {
      const plan = await apiPostActions({
        action: request.action,
        targets: request.targets,
        budgetMinorUnits: request.budgetMinorUnits,
        dryRun: true,
        confirm: false,
      });
      setWorkflow({ phase: "preview", request, plan });
    } catch (cause: unknown) {
      setWorkflow({
        phase: "error",
        request,
        message: cause instanceof Error ? cause.message : String(cause),
        writesDisabled: cause instanceof ApiClientError && cause.isWritesDisabled,
      });
    }
  }, []);

  const confirmAction = useCallback(async (): Promise<void> => {
    if (workflow.phase !== "preview") return;
    const request = workflow.request;
    setWorkflow({ phase: "executing", request, plan: workflow.plan });
    try {
      const result = await apiPostActions({
        action: request.action,
        targets: request.targets,
        budgetMinorUnits: request.budgetMinorUnits,
        dryRun: false,
        confirm: true,
      });
      setWorkflow({ phase: "results", request, result });
      // 写后**就地更新**（第十三批 · 用户第 1 条）：只把「已按 Meta 回读刷新」的行补进表格
      // （状态 / 预算），不整表 reload；未刷新的项不假装已改，只提示稍后自动同步。
      const refreshEntries = result.refreshed ?? [];
      const patches: EntityMetricsRowPatch[] = [];
      for (const entry of refreshEntries) {
        if (!entry.refreshed) continue;
        patches.push({
          accountId: entry.accountId,
          level: entry.level,
          id: entry.entityId,
          ...(entry.status === null ? {} : { status: entry.status }),
          ...(entry.effectiveStatus === null ? {} : { effectiveStatus: entry.effectiveStatus }),
          ...(entry.dailyBudgetCents === null ? {} : { dailyBudgetCents: entry.dailyBudgetCents }),
        });
      }
      metrics.patchRows(patches);
      const refreshFailed = refreshEntries.filter((entry) => !entry.refreshed).length;
      const { succeeded, failed, rejected } = result.summary;
      pushToast({
        kind: failed > 0 || rejected > 0 || refreshFailed > 0 ? "warning" : "success",
        title: `${request.label}：成功 ${succeeded} · 失败 ${failed} · 被拦下 ${rejected}`,
        detail:
          refreshFailed > 0
            ? `已就地更新 ${patches.length} 行；另有 ${refreshFailed} 行未能立即刷新（逐条结果里已注明原因），页面稍后自动同步。`
            : succeeded > 0
              ? `已按 Meta 回读的真实值就地更新 ${patches.length} 行（未整表刷新）。`
              : rejected > 0
                ? "被拦下的目标没有发出任何请求；展开结果查看每条原因。"
                : "结果按条列出，可对照 Meta 侧回读。",
        ttlMs: 9000,
      });
      if (failed === 0 && rejected === 0) clearSelection();
    } catch (cause: unknown) {
      const writesDisabled = cause instanceof ApiClientError && cause.isWritesDisabled;
      setWorkflow({
        phase: "error",
        request,
        message: cause instanceof Error ? cause.message : String(cause),
        writesDisabled,
      });
    }
  }, [clearSelection, metrics, pushToast, workflow]);

  const handleRowPause = useCallback(
    (row: AdEntityRow) => {
      void beginAction({
        action: "pause",
        targets: [toTarget(row)],
        budgetMinorUnits: null,
        label: `暂停「${row.name}」`,
      });
    },
    [beginAction],
  );

  const handleRowActivate = useCallback(
    (row: AdEntityRow) => {
      void beginAction({
        action: "activate",
        targets: [toTarget(row)],
        budgetMinorUnits: null,
        label: `启动「${row.name}」`,
      });
    },
    [beginAction],
  );

  const handleRowBudget = useCallback((row: AdEntityRow) => {
    setBudgetTargets([row]);
    setBudgetInitialValue(budgetInputValue(row));
  }, []);

  const handleBulkPause = useCallback(() => {
    if (selectedRows.length === 0) return;
    void beginAction({
      action: "pause",
      targets: selectedRows.map(toTarget),
      budgetMinorUnits: null,
      label: `批量暂停 ${selectedRows.length} 个目标`,
    });
  }, [beginAction, selectedRows]);

  const handleBulkActivate = useCallback(() => {
    if (selectedRows.length === 0) return;
    void beginAction({
      action: "activate",
      targets: selectedRows.map(toTarget),
      budgetMinorUnits: null,
      label: `批量启动 ${selectedRows.length} 个目标`,
    });
  }, [beginAction, selectedRows]);

  const handleBulkBudget = useCallback(
    (dailyBudgetCents: number) => {
      const targets = selectedRows.filter((row) => supportsBudget(row.level));
      if (targets.length === 0) return;
      setBudgetTargets(targets);
      setBudgetInitialValue((dailyBudgetCents / 100).toFixed(2));
    },
    [selectedRows],
  );

  const submitBudget = useCallback(
    (minorUnits: number) => {
      const targets = budgetTargets;
      setBudgetTargets(null);
      if (targets === null || targets.length === 0) return;
      void beginAction({
        action: "set_daily_budget",
        targets: targets.map(toTarget),
        budgetMinorUnits: minorUnits,
        label: `改日预算 × ${targets.length}`,
      });
    },
    [beginAction, budgetTargets],
  );

  const closeWorkflow = useCallback(() => setWorkflow({ phase: "idle" }), []);

  // 滚动基准（沿用旧表的 __AMS_PERF__ 契约，无人值守脚本仍可用）
  const handleScrollElement = useCallback((element: HTMLDivElement | null) => {
    scrollElementRef.current = element;
  }, []);

  useEffect(() => {
    if (metrics.state !== "ready" || scrollElementRef.current === null) {
      registerScrollBenchRunner(null);
      return;
    }
    const runner = async () => {
      const element = scrollElementRef.current;
      if (element === null) throw new Error("滚动容器未就绪");
      const result = await runScrollBench({
        element,
        totalRows: sorted.length,
        rowHeight,
        durationMs: 5000,
        warmupMs: 600,
      });
      setLatestBenchResult(result);
      pushToast({
        kind: result.p95FrameMs <= 16.7 ? "success" : "warning",
        title: `滚动基准完成：平均 ${formatFixed(result.avgFps, 1)}fps · p95 ${formatFixed(result.p95FrameMs, 1)}ms`,
        detail: `DOM 行节点 ${result.renderedRows} / 总行 ${result.totalRows}`,
        ttlMs: 10000,
      });
      return result;
    };
    registerScrollBenchRunner(runner);
    return () => registerScrollBenchRunner(null);
  }, [metrics.state, pushToast, rowHeight, sorted.length]);

  useEffect(() => {
    if (metrics.state !== "ready") return;
    window.__AMS_PERF__ = {
      totalRows: () => sorted.length,
      renderedRows: () => document.querySelectorAll("[data-row-index]").length,
      run: (durationMs = 5000, stepPx = 48) =>
        runScrollBench({
          element: scrollElementRef.current ?? document.body,
          totalRows: sorted.length,
          rowHeight,
          durationMs,
          warmupMs: 600,
          stepPx,
        }),
      last: () => getLatestBenchResult(),
      setDensity: (mode) => useUiStore.getState().setDensity(mode),
      routeTransition: () => rum.snapshot(),
    };
    return () => {
      delete window.__AMS_PERF__;
    };
  }, [metrics.state, rowHeight, sorted.length]);

  // ------------------------------------------------------------------ 渲染

  // UTM 参考口径的覆盖率：列头 badge（短）+ 信息条（完整），否则 utmRoas 会被当全量口径。
  const workflowOpen = workflow.phase !== "idle";
  const dialogMode = workflow.phase === "results" ? "results" : "preview";
  const dialogPlan = workflow.phase === "preview" || workflow.phase === "executing" ? workflow.plan : workflow.phase === "results" ? workflow.result : null;
  const dialogTitle =
    workflow.phase === "idle"
      ? ""
      : workflow.phase === "results"
        ? `写入结果：${ACTION_LABEL[workflow.request.action]}`
        : `确认${ACTION_LABEL[workflow.request.action]}？`;
  const dialogLabel = workflow.phase === "idle" ? "" : workflow.request.label;
  const dialogError = workflow.phase === "error" ? workflow.message : null;
  const dialogWritesDisabled = workflow.phase === "error" ? workflow.writesDisabled : false;

  const requiredNotice = shopRequiredNotice(requirement);
  if (shopRequiredBlocksContent(requirement) && requiredNotice !== null) {
    return (
      <div className="p-6" data-testid="shop-required-notice" data-shop-requirement={requirement.kind}>
        <h2 className="text-body font-semibold text-ink">{requiredNotice.title}</h2>
        <p className="mt-1 text-meta text-ink-2">{requiredNotice.description}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-page">
      {/* 「仍在结算」提示条（2026-10-05 第十七批）：warning 语义，不藏数字；不适用时返回 null。 */}
      <SettlementWarning readiness={readiness} className="rounded-lg border border-warning/40 bg-warning-subtle px-3 py-2 text-2xs text-warning-text" />
      {/* console 的页头：标题 + 当前层级 chip + 口径说明，右侧是行数统计（不占工具条） */}
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold text-ink">广告实体</h2>
        <Chip tone="info">{LEVELS.find((option) => option.value === level)?.label ?? level}</Chip>
        <span className="text-meta text-ink-3">
          数据源 GET /v1/metrics/entities（{range.from} ~ {range.to}）· 写操作必须 dry-run → 确认 → 执行
        </span>
        <span className="ml-auto text-meta text-ink-3 tnum">
          已加载 {rows.length} 行 · 筛选后 {filtered.length} 行 · 已选 {selection.size}
        </span>
      </div>
      {/* 每个视图都要显示「数据齐到哪天」（用户口径第 3 条）。 */}
      <DataReadinessNote readiness={readiness} testId="ads-readiness" className="-mt-2 text-meta text-ink-3" />

      {/* 表格卡片：console 的「卡片包住工具条 + 数据区」结构（圆角/描边/底色都走 token） */}
      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden border border-line-subtle"
        style={{ background: "var(--bg-surface)", borderRadius: "var(--radius-lg)" }}
      >
        <div className="flex flex-wrap items-center gap-2 border-b border-line-subtle px-3 py-2">
          <div className="flex overflow-hidden rounded-control border border-line" role="group" aria-label="实体层级">
            {LEVELS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => changeLevel(option.value)}
                aria-pressed={level === option.value}
                className={`px-2 text-meta transition-colors dur-fast ${
                  level === option.value ? "bg-accent-subtle text-ink" : "text-ink-2 hover:bg-hover"
                }`}
                style={{ height: "var(--control-height)" }}
              >
                {option.label}
              </button>
            ))}
          </div>

          <select
            value={windowDays}
            aria-label="指标窗口"
            onChange={(event) => setWindowDays(Number(event.target.value))}
            className="rounded-control border border-line bg-surface px-2 text-table text-ink outline-none"
            style={{ height: "var(--control-height)" }}
          >
            {WINDOW_PRESETS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <input
            value={queryInput}
            onChange={(event) => setQueryInput(event.target.value)}
            placeholder="搜索名称 / 账户 / id（仅已加载行）"
            aria-label="搜索实体"
            className="w-56 rounded-control border border-line bg-surface px-2 text-table text-ink outline-none focus-visible:border-accent"
            style={{ height: "var(--control-height)" }}
          />

          <select
            value={filter.status}
            aria-label="状态筛选"
            onChange={(event) => setFilter((previous) => ({ ...previous, status: event.target.value }))}
            className="rounded-control border border-line bg-surface px-2 text-table text-ink outline-none"
            style={{ height: "var(--control-height)" }}
          >
            <option value="all">全部状态</option>
            {statuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>

          <select
            value={filter.account}
            aria-label="账户筛选"
            onChange={(event) => setFilter((previous) => ({ ...previous, account: event.target.value }))}
            className="rounded-control border border-line bg-surface px-2 text-table text-ink outline-none"
            style={{ height: "var(--control-height)" }}
          >
            <option value="all">全部账户（{accounts.length}）</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.label}（{account.rowCount}）
              </option>
            ))}
          </select>

          {/* 语言筛选（R24）：服务端过滤（campaign 直判 / adset·ad 沿父级解析），写进 URL。 */}
          <select
            value={search.language}
            aria-label="语言筛选"
            onChange={(event) => changeLanguage(event.target.value)}
            className="rounded-control border border-line bg-surface px-2 text-table text-ink outline-none"
            style={{ height: "var(--control-height)" }}
          >
            {LANGUAGES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={reload}
            className="rounded-control border border-line px-2 text-meta text-ink-2 transition-colors dur-fast hover:bg-hover"
            style={{ height: "var(--control-height)" }}
          >
            重新拉取
          </button>

          <span className="ml-auto text-2xs text-ink-3 tnum">显示 {sorted.length} 行</span>
        </div>

        <div className="border-b border-line-subtle bg-sunken px-3 py-1 text-2xs text-ink-2">
          {entityLevelHint(level)} · 指标来自 GET /v1/metrics/entities（{range.from} ~ {range.to}，花费降序）；筛选与排序<b>只作用于已加载行</b>。
          语言筛选（{language === undefined ? "全部" : language}）走<b>服务端</b>：按广告系列的语言过滤，广告组/广告沿所属系列判定。
          ROAS 为<b>平台自报口径</b>（Meta 报的转化价值 ÷ 花费），可能高于店铺真实水平（含浏览归因与跨设备）；
          判断整体加/减投放请用「经营概览」的<b>店铺整体 ROAS</b>。
        </div>

        {/* 诊断告警（task-33）：人话在前、原码收折叠区；未知码不吞。共享 `lib/diagnostics.tsx`。 */}
        <DiagnosticWarnings
          warnings={metrics.warnings}
          className="border-b border-line-subtle bg-warning-subtle px-3 py-1 text-2xs text-warning-text"
        />

        <div className="flex min-h-0 flex-1 flex-col">
          {metrics.state === "loading" ? (
            <TableSkeleton rows={14} gridTemplate={ENTITY_GRID_TEMPLATE} width={ENTITY_TABLE_WIDTH} />
          ) : metrics.state === "error" ? (
            <StateView
              kind="error"
              title="实体数据加载失败"
              description={describeError(metrics.error)}
              actionLabel="重试"
              onAction={reload}
            />
          ) : rows.length === 0 ? (
            <StateView
              kind="empty"
              title="还没有广告实体数据"
              description="当前工作区尚未同步到任何 Meta 实体，或该层级确实为空。请先触发一次同步；本页不会用示例数据填充。"
              actionLabel="重新拉取"
              onAction={reload}
            />
          ) : sorted.length === 0 ? (
            <StateView
              kind="empty"
              title="没有符合筛选条件的实体"
              description={`当前条件下 0 行（已加载 ${rows.length} 行）。可以放宽条件或清除筛选。`}
              actionLabel="清除筛选"
              onAction={() => {
                setQueryInput("");
                setFilter(DEFAULT_ENTITY_FILTER);
              }}
            />
          ) : (
            <EntityTable
              rows={sorted}
              rowHeight={rowHeight}
              overscan={OVERSCAN}
              selectedIds={selection}
              focusedIndex={focusedIndex}
              sortId={sortId}
              sortDir={sortDir}
              pageStep={PAGE_STEP}
              busy={busy}
              hasMore={metrics.hasMore}
              loadingMore={metrics.loadingMore}
              onSort={handleSort}
              onFocusIndex={setFocusedIndex}
              onToggleSelect={toggleSelect}
              onExtendSelection={extendSelection}
              onSelectAll={selectAll}
              onClearSelection={clearSelection}
              onPause={handleRowPause}
              onActivate={handleRowActivate}
              onEditBudget={handleRowBudget}
              onLoadMore={metrics.loadMore}
              onScrollElement={handleScrollElement}
            />
          )}
        </div>
      </div>

      {selection.size === 0 ? null : (
        <BulkActionBar
          selectedCount={selection.size}
          accountCount={new Set(selectedRows.map((row) => row.accountId)).size}
          busy={busy}
          onPause={handleBulkPause}
          onResume={handleBulkActivate}
          onBudget={handleBulkBudget}
          onClear={clearSelection}
          budgetEnabled={budgetEligible}
          budgetHint={budgetEligible ? null : "选中项含广告层实体：广告层没有预算字段"}
          currency={selectedRows[0]?.currency ?? "USD"}
        />
      )}

      {budgetTargets === null ? null : (
        <BudgetDialog
          open
          targets={budgetTargets}
          initialValue={budgetInitialValue}
          busy={workflow.phase === "dry-run"}
          onClose={() => setBudgetTargets(null)}
          onSubmit={submitBudget}
        />
      )}

      <ActionPlanDialog
        open={workflowOpen}
        mode={dialogMode}
        title={dialogTitle}
        requestLabel={dialogLabel}
        plan={dialogPlan}
        busy={busy}
        errorMessage={dialogError}
        writesDisabled={dialogWritesDisabled}
        onConfirm={() => void confirmAction()}
        onClose={closeWorkflow}
      />
    </div>
  );
}
